import React, { useState, useEffect } from 'react';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Pagination,
  Alert,
  CircularProgress,
  Tooltip,
  InputAdornment,
  useTheme
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  PersonAdd as PersonAddIcon,
  VerifiedUser as PermissionIcon
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { UserGroup } from '../../types/userGroup';
import { userGroupsApi } from '../../services/userGroupService';

const UserGroupList: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // فلاتر وتصفح
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [limit] = useState(10);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'active' | 'inactive' | ''>('');
  
  // حوار الحذف
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<UserGroup | null>(null);
  const [deleting, setDeleting] = useState(false);
  
  // تحميل البيانات
  const loadUserGroups = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await userGroupsApi.getUserGroups(page, limit, search, status);
      setUserGroups(response.userGroups);
      setTotalPages(response.pagination.pages);
      setTotalCount(response.pagination.total);
    } catch (err) {
      console.error('Error loading user groups:', err);
      setError('حدث خطأ أثناء تحميل مجموعات المستخدمين');
    } finally {
      setLoading(false);
    }
  };
  
  // تحميل البيانات عند تغيير المعايير
  useEffect(() => {
    loadUserGroups();
  }, [page, limit, status]);
  
  // البحث عند الضغط على Enter
  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      setPage(1); // إعادة التصفح للصفحة الأولى
      loadUserGroups();
    }
  };
  
  // تغيير الصفحة
  const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };
  
  // تحديث الفلتر
  const applyFilters = () => {
    setPage(1);
    loadUserGroups();
  };
  
  // إعادة تعيين الفلاتر
  const resetFilters = () => {
    setSearch('');
    setStatus('');
    setPage(1);
    // سيتم تشغيل loadUserGroups() تلقائيًا بسبب تغيير الحالة
  };
  
  // فتح حوار الحذف
  const openDeleteDialog = (group: UserGroup) => {
    setGroupToDelete(group);
    setDeleteDialogOpen(true);
  };
  
  // حذف مجموعة
  const confirmDelete = async () => {
    if (!groupToDelete) return;
    
    try {
      setDeleting(true);
      await userGroupsApi.deleteUserGroup(groupToDelete._id);
      setDeleteDialogOpen(false);
      setGroupToDelete(null);
      loadUserGroups(); // إعادة تحميل البيانات
    } catch (err) {
      console.error('Error deleting user group:', err);
      setError('حدث خطأ أثناء حذف المجموعة');
    } finally {
      setDeleting(false);
    }
  };
  
  return (
    <Box sx={{ p: 3 }}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 3, 
          borderRadius: 2,
          boxShadow: theme.shadows[2],
          mb: 4,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* شريط زخرفي علوي */}
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 8,
            bgcolor: 'primary.main',
          }}
        />
        
        {/* رأس الصفحة */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 1 }}>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupIcon fontSize="large" color="primary" />
            مجموعات المستخدمين
          </Typography>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            component={Link}
            to="/admin/user-groups/new"
            sx={{ borderRadius: 2 }}
            disabled={!hasPermission('admin', 'user-groups', 'create')}
          >
            إضافة مجموعة جديدة
          </Button>
        </Box>
        
        {/* فلاتر البحث */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: 2, 
            mb: 3, 
            p: 2, 
            borderRadius: 2,
            bgcolor: 'background.default'
          }}
        >
          <TextField
            label="بحث"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={handleSearchKeyDown}
            sx={{ flexGrow: 1, minWidth: '200px' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
              sx: { borderRadius: 2 }
            }}
          />
          
          <TextField
            select
            label="الحالة"
            variant="outlined"
            size="small"
            value={status}
            onChange={(e) => setStatus(e.target.value as 'active' | 'inactive' | '')}
            sx={{ minWidth: '150px' }}
            InputProps={{
              sx: { borderRadius: 2 }
            }}
          >
            <MenuItem value="">جميع الحالات</MenuItem>
            <MenuItem value="active">نشط</MenuItem>
            <MenuItem value="inactive">غير نشط</MenuItem>
          </TextField>
          
          <Button
            variant="outlined"
            color="primary"
            onClick={applyFilters}
            startIcon={<FilterListIcon />}
            sx={{ borderRadius: 2 }}
          >
            تطبيق
          </Button>
          
          <Button
            variant="outlined"
            color="secondary"
            onClick={resetFilters}
            startIcon={<RefreshIcon />}
            sx={{ borderRadius: 2 }}
          >
            إعادة تعيين
          </Button>
        </Box>
        
        {/* عرض الخطأ إن وجد */}
        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* جدول مجموعات المستخدمين */}
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead sx={{ bgcolor: 'background.default' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>اسم المجموعة</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>الوصف</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>تاريخ الإنشاء</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>الإجراءات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" sx={{ mt: 2 }}>
                      جاري تحميل البيانات...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : userGroups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1">
                      لا توجد مجموعات مستخدمين متاحة
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                userGroups.map(group => (
                  <TableRow key={group._id} hover>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.description || '-'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={group.status === 'active' ? 'نشط' : 'غير نشط'} 
                        color={group.status === 'active' ? 'success' : 'default'}
                        size="small"
                        sx={{ borderRadius: 1 }}
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(group.createdAt).toLocaleDateString('ar-SA')}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        {hasPermission('admin', 'user-groups', 'edit') && (
                          <Tooltip title="تعديل المجموعة">
                            <IconButton
                              color="primary"
                              size="small"
                              onClick={() => navigate(`/admin/user-groups/edit/${group._id}`)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {hasPermission('admin', 'user-groups', 'manage_permissions') && (
                          <Tooltip title="إدارة الصلاحيات">
                            <IconButton
                              color="secondary"
                              size="small"
                              onClick={() => navigate(`/admin/permissions/${group._id}`)}
                            >
                              <PermissionIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        {hasPermission('admin', 'user-groups', 'delete') && (
                          <Tooltip title="حذف المجموعة">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => openDeleteDialog(group)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* التصفح */}
        {!loading && userGroups.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              إجمالي النتائج: {totalCount}
            </Typography>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Paper>
      
      {/* حوار تأكيد الحذف */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !deleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>تأكيد حذف المجموعة</DialogTitle>
        <DialogContent>
          <DialogContentText>
            هل أنت متأكد من رغبتك في حذف مجموعة "{groupToDelete?.name}"؟
            <br />
            هذا الإجراء لا يمكن التراجع عنه، وسيتم حذف جميع الصلاحيات المرتبطة بهذه المجموعة.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            color="primary"
            disabled={deleting}
          >
            إلغاء
          </Button>
          <Button 
            onClick={confirmDelete} 
            color="error" 
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
          >
            {deleting ? 'جاري الحذف...' : 'تأكيد الحذف'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserGroupList;